import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import type {} from "@mui/x-data-grid/themeAugmentation";
import DeviceApi from "../../api/device";
import {
	DeviceStatusWithDisplayDto,
	FetchDeviceWithHistoryDto,
} from "../../models/Device";
import { AppContext } from "../../Contexts/AppContext";
import { useBus, useListener } from "react-bus";
import AddDeviceModal from "./components/AddDeviceModal/AddDeviceModal";
import { UserRole } from "../../models/User";
import DevicesTable from "./components/DevicesTable/DevicesTable";
import DeviceStatusSelector from "../../common/components/DeviceStatusSelector/DeviceStatusSelector";
import PermissionaryConfigurationApi from "../../api/permissionary-configuration";
import { PermissionaryConfiguration } from "../../models/PermissionaryConfiguration";
import { getDerivedDeviceStatus } from "../../utils/deviceStatus";
import { DeviceStatus } from "../../models/Device";
import CabinetStateSelector from "../../common/components/CabinetStateSelector/CabinetStateSelector";
import { CabinetState } from "../../models/DeviceHistory";
import { CabinetStateWithDisplayDto } from "../../common/components/CabinetStateSelector/types";

export default function DevicesScreen() {
	document.title = "Dispositivos";
	const bus = useBus();
	const [devices, setDevices] = useState<FetchDeviceWithHistoryDto[]>([]);
	const [deviceStatus, setDeviceStatus] = useState<string>("all");
	const [cabinetState, setCabinetState] = useState<CabinetState | "all">("all");
	const [permissionaryConfiguration, setPermissionaryConfiguration] =
		useState<PermissionaryConfiguration | null>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const { setTitle, userContext } = useContext(AppContext);

	const fetchPermissionaryConfiguration = useCallback(async () => {
		if (!userContext?.permissionaryId) {
			setPermissionaryConfiguration(null);
			return;
		}
		try {
			const result: PermissionaryConfiguration =
				await PermissionaryConfigurationApi.fetchConfiguration(
					userContext.permissionaryId,
					userContext.accessToken,
				);
			setPermissionaryConfiguration(result);
		} catch (error) {
			setPermissionaryConfiguration(null);
		}
	}, [userContext]);

	const fetchDevices = useCallback(async () => {
		if (!userContext?.permissionaryId) {
			setDevices([]);
			return;
		}

		try {
			setLoading(true);
			const response = await DeviceApi.listDevices({
				token: localStorage.getItem("accessToken"),
				query: {
					permissionaryId: userContext.permissionaryId,
				},
			});

			setDevices(response);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [userContext]);

	const handleRefresh = useCallback(async () => {
		await Promise.all([fetchDevices(), fetchPermissionaryConfiguration()]);
	}, [fetchDevices, fetchPermissionaryConfiguration]);

	const deviceStatusOnChange = useCallback(
		(deviceStatusWithDisplay: DeviceStatusWithDisplayDto) => {
			setDeviceStatus(deviceStatusWithDisplay.status);
		},
		[setDeviceStatus],
	);

	const filteredDevices = useMemo(() => {
		return devices.filter((device) => {
			const matchesDeviceStatus =
				deviceStatus === DeviceStatus.ALL ||
				getDerivedDeviceStatus(device, permissionaryConfiguration) === deviceStatus;
			const matchesCabinetState =
				cabinetState === "all" ||
				(device.history.length > 0 &&
					device.history[0].cabinetState === cabinetState);

			return matchesDeviceStatus && matchesCabinetState;
		});
	}, [cabinetState, deviceStatus, devices, permissionaryConfiguration]);

	useListener("reloadDevicesScreenData", fetchDevices);

	useEffect(() => {
		setTitle("Dispositivos");
	}, [setTitle]);

	useEffect(() => {
		fetchDevices();
	}, [fetchDevices]);

	useEffect(() => {
		fetchPermissionaryConfiguration();
	}, [fetchPermissionaryConfiguration]);

	return (
		<Box
			sx={{
				minHeight: "100%",
				width: "100%",
				px: { xs: 2, md: 4 },
				py: { xs: 3, md: 4 },
				background:
					"radial-gradient(1200px 600px at 10% -10%, rgba(4, 153, 255, 0.16), transparent 60%), radial-gradient(1000px 600px at 100% 0%, rgba(0, 198, 174, 0.12), transparent 60%), linear-gradient(180deg, #f5f7fb 0%, #edf2f7 100%)",
			}}>
			<Box
				sx={{
					maxWidth: 1200,
					mx: "auto",
					borderRadius: 4,
					border: "1px solid rgba(18, 40, 72, 0.08)",
					backgroundColor: "rgba(255, 255, 255, 0.9)",
					boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
					backdropFilter: "blur(12px)",
					p: { xs: 2.5, md: 4 },
				}}>
				<Box
					sx={{
						display: "flex",
						alignItems: { xs: "flex-start", md: "center" },
						justifyContent: "space-between",
						gap: 2,
						flexWrap: "wrap",
						mb: 3,
					}}>
					<Box sx={{ textAlign: "left" }}>
						<Typography
							variant='h4'
							sx={{
								fontWeight: 700,
								color: "#0f172a",
								letterSpacing: "-0.02em",
							}}>
							Dispositivos
						</Typography>
						<Typography
							variant='body2'
							sx={{ color: "rgba(15, 23, 42, 0.7)", mt: 0.5 }}>
							Gestiona el inventario y el estado operativo en tiempo real.
						</Typography>
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							flexWrap: "wrap",
							justifyContent: "flex-end",
						}}>
						<Box sx={{ minWidth: 220 }}>
							<DeviceStatusSelector
								reactOnChange={(newDeviceStatusWithDisplay) =>
									deviceStatusOnChange(newDeviceStatusWithDisplay)
								}
							/>
						</Box>
						<Box sx={{ minWidth: 220 }}>
							<CabinetStateSelector
								reactOnChange={(
									newCabinetStateWithDisplay: CabinetStateWithDisplayDto,
								) => {
									setCabinetState(newCabinetStateWithDisplay.state);
								}}
							/>
						</Box>
						<Button
							variant='outlined'
							startIcon={<RefreshIcon />}
							onClick={handleRefresh}
							disabled={loading}
							sx={{
								borderRadius: 999,
								px: 2.4,
								py: 1.1,
								fontWeight: 700,
								letterSpacing: "0.03em",
								borderColor: "rgba(2, 132, 199, 0.4)",
								color: "#0369a1",
								"&:hover": {
									borderColor: "rgba(2, 132, 199, 0.75)",
									backgroundColor: "rgba(2, 132, 199, 0.06)",
								},
							}}>
							Refrescar
						</Button>
						{userContext?.role === UserRole.ADMIN && (
							<Button
								variant='contained'
								startIcon={<AddIcon />}
								onClick={() => {
									bus.emit("openAddDeviceModal");
								}}
								sx={{
									borderRadius: 999,
									px: 3,
									py: 1.2,
									fontWeight: 700,
									letterSpacing: "0.04em",
									background:
										"linear-gradient(135deg, #0ea5e9 0%, #0284c7 55%, #0369a1 100%)",
									boxShadow: "0 12px 20px rgba(2, 132, 199, 0.25)",
									"&:hover": {
										background:
											"linear-gradient(135deg, #0284c7 0%, #0369a1 70%, #075985 100%)",
									},
								}}>
								Agregar
							</Button>
						)}
					</Box>
				</Box>
				{loading ? (
					<Box
						sx={{
							textAlign: "center",
							py: 8,
							color: "rgba(15, 23, 42, 0.65)",
						}}>
						<CircularProgress size={72} thickness={4.2} />
						<Typography sx={{ mt: 2, fontWeight: 500 }}>
							Cargando dispositivos...
						</Typography>
					</Box>
				) : (
					<DevicesTable
						devices={filteredDevices}
						permissionaryConfiguration={permissionaryConfiguration}
					/>
				)}
			</Box>
			<AddDeviceModal />
		</Box>
	);
}
